'use client';
import { Logo } from '@/components/global/logo';
import Link from 'next/link';
import { CallToAction } from '@/components/call-to-action';
import { usePathname } from 'next/navigation';
import { ROUTES } from '@/routes';
import { GLOBAL_NAV, SOCIAL_LINKS } from '@/utils/constants';
import { IconChevronRight } from '@/components/icons';

type Props = {
  className?: string;
};

export const Footer: React.FC<Props> = ({ className = '' }) => {
  const pathname = usePathname();

  const showCta = pathname !== ROUTES.CONTACT;
  const isChildOfWork = pathname.startsWith('/work/') && pathname !== '/work/';

  return (
    <footer
      className={`px-1 pb-0 md:px-4 md:pb-4 ${className} ${
        showCta && isChildOfWork
          ? 'mt-10 md:mt-10 lg:mt-20 xl:mt-20'
          : showCta && 'mt-40 md:mt-44 lg:mt-56 xl:mt-64'
      }`}>
      <div className="bg-dark pointer-events-none rounded-2xl dark:bg-black">
        <div className="pointer-events-auto relative container">
          {showCta && <CallToAction className="-mb-20 -translate-y-1/2" />}
        </div>

        <div className="pointer-events-auto container">
          <div className="relative auto-cols-[1fr] grid-cols-[0.5fr_0.5fr] justify-between gap-[8vw] lg:grid">
            <Link href={ROUTES.ROOT} className="block">
              <Logo
                mode="logo"
                className="h-10 opacity-30"
                text="light"
                iconClass="text-cream"
              />
            </Link>
            <div>
              <div className="mt-20 flex flex-wrap justify-start gap-[8vw] self-start lg:mt-0 lg:flex-nowrap">
                <div className="min-w-46 flex-1">
                  <h3 className="text-cream/30 px-2 text-sm font-bold tracking-wider uppercase">
                    Navigate
                  </h3>
                  <ul className="mt-4">
                    {GLOBAL_NAV.map((item: any, index: number) => {
                      const isSamePage = pathname === item.href;
                      return (
                        <li key={item.label}>
                          <Link
                            className="text-cream/80 relative flex items-center gap-1 rounded p-2 transition-all duration-300 ease-in-out hover:gap-2 hover:bg-white/10 hover:text-white"
                            href={item.href}
                            onClick={e => {
                              if (isSamePage) {
                                e.preventDefault();
                                window.scrollTo({
                                  top: 0,
                                  behavior: 'smooth',
                                });
                              }
                            }}>
                            {item.label}
                            <IconChevronRight className="size-4" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="min-w-46 flex-1">
                  <h3 className="text-cream/30 px-2 text-sm font-bold tracking-wider uppercase">
                    Social
                  </h3>
                  <ul className="mt-4">
                    {SOCIAL_LINKS.map((item: any, index: number) => {
                      return (
                        <li key={item.label}>
                          <Link
                            className="text-cream/80 relative flex items-center gap-1 rounded p-2 transition-all duration-300 ease-in-out hover:gap-2 hover:bg-white/10 hover:text-white"
                            href={item.href}
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label={`${item.label} (opens in a new tab)`}>
                            {item.label}
                            <IconChevronRight className="size-4" />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="border-cream/10 text-cream/50 mt-20 items-center border-t py-3 text-sm">
            Copyright &copy; {new Date().getFullYear()} RayLo. All rights
            reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};
